/**
 * User-friendly API error messages (Phase 4.2).
 * Use in catch blocks: setError(getApiErrorMessage(err))
 * or display in toasts/alerts.
 */

/**
 * Returns a human-readable message for API errors (403, 503, timeout, network, etc.).
 * @param {Error} err - Axios error or standard Error
 * @param {string} fallback - Optional fallback when no specific message applies
 * @returns {string}
 */
export function getApiErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;

  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  const code = err.code;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const msg = detail
      .map((e) => (e?.loc ? `${e.loc.join('.')}: ` : '') + (e?.msg || ''))
      .filter(Boolean)
      .join('; ');
    if (msg) return msg;
  }
  if (detail?.message) {
    return detail.message;
  }

  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return 'The requested resource was not found.';
  }
  if (status === 503) {
    return 'Service temporarily unavailable. Please try again in a moment.';
  }
  if (status === 422) {
    return detail?.msg || 'Validation error. Please check your input.';
  }
  if (status >= 500) {
    return 'Server error. Please try again later.';
  }

  const requestUrl = (err.config?.baseURL || '') + (err.config?.url || '');
  const isProductionHost =
    typeof window !== 'undefined' && /archonpm\.com|render\.com/i.test(window.location.hostname);
  const isLoginCall = /\/(token|login)\b/i.test(requestUrl);

  if (code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout')) {
    if (isLoginCall && isProductionHost) {
      return 'The server is waking up after inactivity (Render cold start). Please wait a few seconds and try logging in again.';
    }
    if (isProductionHost) {
      return 'The request took too long. The server may be waking up; please try again in a moment.';
    }
    return 'Request timed out. If running locally, ensure the backend and PostgreSQL are running and DATABASE_URL in backend/.env points to your local database.';
  }
  if (code === 'ERR_NETWORK' || err.message?.toLowerCase().includes('network')) {
    return 'Network error. Please check your connection and try again.';
  }

  return err.message && err.message !== 'Network Error' ? err.message : fallback;
}
